/**
 * Basket minimum-investment-size math.
 *
 * For a basket of stocks with signed weights, lot sizes (minSize), and prices,
 * computes:
 *  - theoreticalMin:     hard floor -- the smallest basket size where every
 *                         contract clears 1 lot at its target weight. Formula:
 *                         max_i( M_i / |w_i| ), where M_i = minSize_i * price_i.
 *  - practicalMin:       theoreticalMin / (2 * tolerance). Guarantees per-contract
 *                         rounding error <= tolerance (relative to target weight)
 *                         when rounding fractional lots to the nearest whole lot.
 *  - freeReweightMin:    sum_i(M_i). The absolute floor achievable with ANY
 *                         weighting (i.e. hold exactly 1 lot of each contract).
 *  - bindingContract:    the contract driving theoreticalMin.
 *  - perContract:        per-contract breakdown at practicalMin (lot counts,
 *                         actual weight, tracking error).
 *  - cashDrag:           leftover dollars at practicalMin after lot-rounding.
 *
 * Signed weights follow the codebase convention (weight in [-100, 100]; sign
 * carries position direction). The min-size calculation uses |weight|; sign
 * does not affect the minimum.
 *
 * Cash positions (CASH_CONID) are excluded from the calculation: cash has no
 * lot size constraint.
 */

export const BASKET_MIN_CASH_CONID = 141175785;

export const DEFAULT_TOLERANCE = 0.0001;
export const MIN_TOLERANCE = 0.0001;
export const MAX_TOLERANCE = 0.5;

export interface BasketMinInvestmentStock {
  conId: number;
  ticker: string;
  name?: string;
  /** Signed weight in the basket, in percent units (-100..100). */
  weight: number;
  /** Lot size (minimum tradable quantity). */
  minSize: number;
  /** Per-unit price (in USD). */
  price: number;
  /** If false, the contract is excluded from the scenario (without removal). */
  enabled?: boolean;
}

export interface MinInvestmentContractBreakdown {
  conId: number;
  ticker: string;
  name?: string;
  weight: number;
  weightAbs: number;
  minSize: number;
  price: number;
  /** M_i = minSize * price -- minimum dollar ticket for one lot. */
  lotTicket: number;
  /** M_i / |w_i| -- the per-contract basket floor. */
  ratio: number;
  /** ratio / theoreticalMin -- fraction of the min driven by this contract. */
  contribution: number;
  /** Target lots at practicalMin (fractional). */
  targetLots: number;
  /** Actual integer lots at practicalMin (rounded, min 1). */
  actualLots: number;
  /** actualLots * lotTicket. */
  actualDollars: number;
  /** actualDollars / practicalMin. */
  actualWeight: number;
  /** actualWeight - |w_i| in percentage points. */
  errorPercentagePoints: number;
  /** |actualWeight - |w_i|| / |w_i| -- relative error. */
  errorRelative: number;
}

export interface BasketMinInvestmentResult {
  theoreticalMin: number;
  practicalMin: number;
  freeReweightMin: number;
  bindingContract: MinInvestmentContractBreakdown;
  perContract: MinInvestmentContractBreakdown[];
  cashDrag: number;
  activeCount: number;
  /** Sum of |weight| across active contracts, in percent. */
  activeWeightSumPct: number;
  /** Tolerance used for practicalMin. */
  tolerance: number;
}

function clampTolerance(tolerance: number): number {
  if (!Number.isFinite(tolerance)) return DEFAULT_TOLERANCE;
  return Math.min(MAX_TOLERANCE, Math.max(MIN_TOLERANCE, tolerance));
}

function isValidStock(s: BasketMinInvestmentStock): boolean {
  if (s.enabled === false) return false;
  if (s.conId === BASKET_MIN_CASH_CONID) return false;
  if (!Number.isFinite(s.weight) || s.weight === 0) return false;
  if (!Number.isFinite(s.minSize) || s.minSize <= 0) return false;
  if (!Number.isFinite(s.price) || s.price <= 0) return false;
  return true;
}

export function computeBasketMinInvestment(
  stocks: BasketMinInvestmentStock[],
  tolerance: number = DEFAULT_TOLERANCE
): BasketMinInvestmentResult | null {
  const tol = clampTolerance(tolerance);
  const active = stocks.filter(isValidStock);
  if (active.length === 0) return null;

  // First pass: lot ticket, per-contract ratio, theoreticalMin, freeReweightMin.
  const partial = active.map((s) => {
    const weightAbs = Math.abs(s.weight) / 100; // fraction
    const lotTicket = s.minSize * s.price;
    const ratio = lotTicket / weightAbs;
    return { s, weightAbs, lotTicket, ratio };
  });

  const theoreticalMin = partial.reduce(
    (acc, p) => (p.ratio > acc ? p.ratio : acc),
    0
  );
  const freeReweightMin = partial.reduce((acc, p) => acc + p.lotTicket, 0);
  const practicalMin = theoreticalMin / (2 * tol);

  // Second pass: per-contract breakdown at practicalMin.
  const perContract: MinInvestmentContractBreakdown[] = partial.map((p) => {
    const targetLots = (practicalMin * p.weightAbs) / p.lotTicket;
    const actualLots = Math.max(1, Math.round(targetLots));
    const actualDollars = actualLots * p.lotTicket;
    const actualWeight = actualDollars / practicalMin;
    const errorPercentagePoints = (actualWeight - p.weightAbs) * 100;
    const errorRelative =
      p.weightAbs > 0 ? Math.abs(actualWeight - p.weightAbs) / p.weightAbs : 0;
    const contribution = theoreticalMin > 0 ? p.ratio / theoreticalMin : 0;
    return {
      conId: p.s.conId,
      ticker: p.s.ticker,
      name: p.s.name,
      weight: p.s.weight,
      weightAbs: p.weightAbs,
      minSize: p.s.minSize,
      price: p.s.price,
      lotTicket: p.lotTicket,
      ratio: p.ratio,
      contribution,
      targetLots,
      actualLots,
      actualDollars,
      actualWeight,
      errorPercentagePoints,
      errorRelative,
    };
  });

  const invested = perContract.reduce((acc, r) => acc + r.actualDollars, 0);
  const cashDrag = practicalMin - invested;
  const bindingContract = perContract.reduce((a, b) => (b.ratio > a.ratio ? b : a));
  const activeWeightSumPct = active.reduce(
    (acc, s) => acc + Math.abs(s.weight),
    0
  );

  return {
    theoreticalMin,
    practicalMin,
    freeReweightMin,
    bindingContract,
    perContract,
    cashDrag,
    activeCount: active.length,
    activeWeightSumPct,
    tolerance: tol,
  };
}

/**
 * Convenience: compute the practicalMin at a different tolerance without
 * recomputing the full breakdown (cheap -- just rescales theoreticalMin).
 */
export function practicalMinAtTolerance(
  theoreticalMin: number,
  tolerance: number
): number {
  const tol = clampTolerance(tolerance);
  return theoreticalMin / (2 * tol);
}
